import { useRef, useState } from "react";
import cn from "classnames";
import { Button, BUTTON_VARIANT } from "components/UI/Button";
import { ArrowDown, ArrowUp, Check, Pen, Trash } from "components/UI/Icons";
import { Input } from "components/UI/Input";
import { useAppDispatch } from "hooks/useAppDispatch";
import { useAppSelector } from "hooks/useAppSelector";
import { useOnClickOutside } from "hooks/useOnClickOutside.hook";
import { moveGroup, removeGroup, renameGroup, selectGroupById, selectGroupIndex, selectGroupsCount } from "store";
import { AppDOM } from "utils/app.utils";
import s from "./GroupHeader.module.scss";

interface GroupHeaderProps {
  groupId: string;
}

export const GroupHeader = ({ groupId }: GroupHeaderProps) => {
  const dispatch = useAppDispatch();
  const group = useAppSelector(selectGroupById(groupId));
  const groupsCount = useAppSelector(selectGroupsCount);
  const groupIndex = useAppSelector(selectGroupIndex(groupId));

  const [openEditName, setOpenEditName] = useState(false);
  const [draftName, setDraftName] = useState("");
  const containerEditRef = useRef(null);

  const isFirst = groupIndex === 0;
  const isLast = groupIndex === groupsCount - 1;

  useOnClickOutside({
    ref: containerEditRef,
    shadowRoot: AppDOM.shadowRootNode,
    handler: () => setOpenEditName(false),
  });

  const handleOpenEdit = () => {
    setDraftName(group?.name ?? "");
    setOpenEditName(true);
  };

  const handleSave = () => {
    if (draftName.trim()) dispatch(renameGroup({ id: groupId, name: draftName.trim() }));
    setOpenEditName(false);
  };

  return (
    <div className={s.header}>
      <div ref={containerEditRef} className={cn(s.editBox, { [s.openInput]: openEditName })}>
        {openEditName ? (
          <>
            <div className={s.inputBox}>
              <Input
                type="text"
                autoFocus={true}
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSave()}
              />
            </div>

            <Button variant={BUTTON_VARIANT.ONLY_ICON} className={s.saveBtn} aria-label="Save" onClick={handleSave}>
              <Check />
            </Button>
          </>
        ) : (
          <>
            <span className={s.name}>{group?.name}</span>

            <Button
              variant={BUTTON_VARIANT.ONLY_ICON_TRANSPARENT}
              className={s.editBtn}
              aria-label="Edit"
              onClick={handleOpenEdit}
            >
              <Pen />
            </Button>
          </>
        )}
      </div>

      {groupsCount !== 1 && (
        <div className={s.actionsBox}>
          {!isLast && (
            <Button
              variant={BUTTON_VARIANT.ONLY_ICON}
              className={s.iconBtn}
              aria-label="Move group down"
              onClick={() => dispatch(moveGroup({ id: groupId, direction: "down" }))}
            >
              <ArrowDown />
            </Button>
          )}

          {!isFirst && (
            <Button
              variant={BUTTON_VARIANT.ONLY_ICON}
              className={s.iconBtn}
              aria-label="Move group up"
              onClick={() => dispatch(moveGroup({ id: groupId, direction: "up" }))}
            >
              <ArrowUp />
            </Button>
          )}

          <Button
            variant={BUTTON_VARIANT.ONLY_ICON}
            className={s.iconBtn}
            aria-label="Delete group"
            onClick={() => dispatch(removeGroup(groupId))}
          >
            <Trash />
          </Button>
        </div>
      )}
    </div>
  );
};
